import "./events-listing-page.scss";
import { Page } from "modules/Page";
import { EventListing, EventListingsApi, eventListingsApi } from "api/EventsListingsApi";
import { debounce } from "modules/debounce";
import eventListingsTemplate from "./EventsListingPage.hbs";

type FilterState = {

    [FilterType.StringQuery]: string;
    [FilterType.Page]: number;
    [FilterType.Language]?: string;
};

type TabKey = "upcoming" | "previous";

type State = {
    [key in TabKey]: {
        eventListings: EventListing[];
        filters: FilterState;
        totalEventListings: number;
        totalPages: number;
    };
};

enum FilterType {
    StringQuery = "q",
    Page = "page",
    Language = "lang"
}

export interface EventListingsQueryParams {
    lang?: string
    page?: number; // default is 1
    q?: string;
    pageID?: string;
    type?: "upcoming" | "previous"; 
}


export class EventsListingPage extends Page<typeof EventsListingPage> {
    public static readonly className = "js-events-listing-page";

    private loadMoreButton: HTMLButtonElement | null = null;
    private eventListElement: HTMLElement | null = null
    private pageID: string;
    private state: State = {
        upcoming: {
            eventListings: [],
            filters: {
                [FilterType.StringQuery]: "",
                [FilterType.Page]: 1,
            },
            totalEventListings: 0,
            totalPages: 0
        },
        previous: {
            eventListings: [],
            filters: {
                [FilterType.StringQuery]: "",
                [FilterType.Page]: 1,
            },
            totalEventListings: 0,
            totalPages: 0
        }
    };

    constructor(
        rootElement: HTMLElement,
        index: number,
        private readonly api: EventListingsApi = eventListingsApi
    ) {
        super(rootElement, index);

        this.pageID = this.rootElement.dataset.pageid || "0";

        this.initCategoriesTabs();

        // Attach event listeners for each tab's load more button
        ["upcoming", "previous"].forEach((tabKey) => {
            const loadMoreButton = rootElement.querySelector<HTMLButtonElement>(`#load-more-button-${tabKey}`);
            if (loadMoreButton) {
                loadMoreButton.addEventListener("click", () => {
                    this.loadNextPage(tabKey as "upcoming" | "previous");
                });
            }
        });

        // Initial load for the default active tab
        this.refreshEventListings(this.getActiveTabKey());
    }
    private getEventListingsHtml(eventListings: EventListing[]) {
        return eventListingsTemplate({
            eventListings

        });
    }

    private getRequestParams(tabKey: TabKey) {
        const filters = this.state[tabKey].filters;
        const params: EventListingsQueryParams = {
            q: filters[FilterType.StringQuery],
            page: filters[FilterType.Page],
            lang: filters[FilterType.Language],
            pageID: this.pageID,
            type: tabKey,
        };
        return params;
    }

    private getActiveTabKey(): TabKey {
        const activeTab = this.rootElement.querySelector(".tab-list li.active");
        return activeTab?.getAttribute("data-item") === "1" ? "previous" : "upcoming";
    }

    private getListElement(tabKey: TabKey): HTMLElement | null {
        const tabIndex = tabKey === "upcoming" ? "0" : "1";
        const tabContainer = this.rootElement.querySelector<HTMLElement>(`.tab-categories > div[data-item="${tabIndex}"]`);
        return tabContainer?.querySelector<HTMLElement>(".list__section") || null;
    }


    private updateCountDisplay(tabKey: TabKey) {
        const showingCountElement = document.getElementById(`showing-count-${tabKey}`);
        const totalCountElement = document.getElementById(`total-count-${tabKey}`);

        if (showingCountElement) {
            showingCountElement.textContent = this.state[tabKey].eventListings.length.toString();
        }

        if (totalCountElement) {
            totalCountElement.textContent = this.state[tabKey].totalEventListings.toString();
        }
    }

    private refreshEventListings = debounce(async (tabKey: TabKey) => {
        this.state[tabKey].filters[FilterType.Page] = 1;

        const params = this.getRequestParams(tabKey); 
        const data = await this.api.getEventListings(params);

        this.state[tabKey].eventListings = data.eventListings;
        this.state[tabKey].totalEventListings = data.totalEventListings;
        this.state[tabKey].totalPages = data.totalPages;

        const listElement = this.getListElement(tabKey);
        if (listElement) {
            listElement.innerHTML = this.getEventListingsHtml(data.eventListings);
        }

        this.updateCountDisplay(tabKey);

        const loadMoreButton = this.rootElement.querySelector<HTMLButtonElement>(`#load-more-button-${tabKey}`);
        if (loadMoreButton) {
            loadMoreButton.style.display = data.totalPages > 1 ? "block" : "none";
        }
    }, 500);


    private loadNextPage = debounce(async () => {
        const tabKey = this.getActiveTabKey();
        this.state[tabKey].filters[FilterType.Page] += 1;

        const params = this.getRequestParams(tabKey);
        const data = await this.api.getEventListings(params);

        this.state[tabKey].eventListings.push(...data.eventListings);


        const listElement = this.getListElement(tabKey);
        if (listElement) {
            listElement.innerHTML += this.getEventListingsHtml(data.eventListings);
        }

        this.updateCountDisplay(tabKey);

        const loadMoreButton = this.rootElement.querySelector<HTMLButtonElement>(`#load-more-button-${tabKey}`);
        if (loadMoreButton) {
            const showingCount = this.state[tabKey].eventListings.length;
            const totalCount = this.state[tabKey].totalEventListings;
            loadMoreButton.style.display = showingCount >= totalCount ? "none" : "block";
        }
    });

    public initCategoriesTabs() {
        const categories = this.rootElement.querySelectorAll(".tab-categories > div");
        const tabButtons = this.rootElement.querySelectorAll(".tab-list li");

        // Handle tab click events
        tabButtons.forEach((tab) => {
            tab.addEventListener("click", () => {
                const isActive = tab.classList.contains("active");
                const tabId = tab.getAttribute("data-item");
                const tabKey = tabId === "1" ? "previous" : "upcoming";

                if (isActive) {
                    return;
                }

                // Close all tabs
                tabButtons.forEach((otherTab) => otherTab.classList.remove("active"));
                categories.forEach((category) => category.classList.remove("active"));

                // Open clicked tab
                tab.classList.add("active");
                const tabContent = this.rootElement.querySelector(`div[data-item="${tabId}"]`);
                tabContent?.classList.add("active");

                // Now refresh with correct tabKey
                this.refreshEventListings(tabKey);
            });
        });
    }
}
